import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@parentpath/i18n';
import type { RecommendationPlacement } from '@parentpath/supabase-client';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useLimits } from '@/hooks/useLimits';
import { calculateBabyAgeParts } from '@/lib/babyAge';
import {
  getActiveRecommendation,
  dismissRecommendation,
} from '@/services/recommendationsService';

export function useRecommendation(placement: RecommendationPlacement) {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const locale: 'de' | 'en' = i18n.language === 'en' ? 'en' : 'de';

  const { appMode, currentSSW, currentPregnancy, lastCompletedPregnancy } = usePregnancyStore();
  const { userTier, isLoading: limitsLoading } = useLimits();
  const isPremium = userTier === 'premium';

  // Determine target mode for this query
  const mode = appMode === 'pregnancy' ? 'pregnancy' : appMode === 'baby' ? 'baby' : null;

  // Compute baby age in months
  let babyAgeMonths: number | null = null;
  if (mode === 'baby') {
    const birthDateStr =
      lastCompletedPregnancy?.actual_birth_date ?? currentPregnancy?.actual_birth_date ?? null;
    if (birthDateStr) {
      const parts = calculateBabyAgeParts(new Date(birthDateStr));
      babyAgeMonths = parts.years * 12 + parts.months;
    }
  }

  const ssw = mode === 'pregnancy' ? currentSSW : null;

  const queryKey = ['recommendation', placement, mode, ssw, babyAgeMonths, locale, isPremium];

  const { data: recommendation, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      getActiveRecommendation({
        placement,
        locale,
        mode: mode!,
        ssw,
        babyAgeMonths,
        isPremium,
      }),
    enabled: mode !== null && !limitsLoading,
    staleTime: 5 * 60 * 1000,
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => dismissRecommendation(id),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, null);
    },
    onError: () => {
      // Revert by refetching
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    recommendation: recommendation ?? null,
    isLoading,
    isPremium,
    canDismiss: isPremium,
    dismiss: (id: string) => dismissMutation.mutate(id),
  };
}
